import { useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, Share, WifiOff, X } from 'lucide-react';
import { registerSW } from 'virtual:pwa-register';
import { Button } from '@/components/ui/primitives';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { isNativeAndroid } from '@/lib/nativeRuntime';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DRAFT_KEY = 'mytag.pwa.update-draft';

function isStandalone(): boolean {
  const iosStandalone = Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

function saveFormDraft(): void {
  if (location.pathname === '/login') return;
  const values = [...document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select')]
    .filter((element) => !['password', 'file', 'hidden'].includes(element instanceof HTMLInputElement ? element.type : ''))
    .map((element, index) => ({
      index,
      id: element.id,
      name: element.getAttribute('name') ?? '',
      value: element.value,
      checked: element instanceof HTMLInputElement ? element.checked : undefined,
    }));
  sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ path: location.pathname, values }));
}

function restoreFormDraft(): void {
  const raw = sessionStorage.getItem(DRAFT_KEY);
  if (!raw) return;
  sessionStorage.removeItem(DRAFT_KEY);
  try {
    const draft = JSON.parse(raw) as { path: string; values: Array<{ index: number; id: string; name: string; value: string; checked?: boolean }> };
    if (draft.path !== location.pathname) return;
    window.setTimeout(() => {
      const controls = [...document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select')];
      for (const saved of draft.values) {
        const control = controls.find((item, index) => index === saved.index && item.id === saved.id && (item.getAttribute('name') ?? '') === saved.name);
        if (!control) continue;
        const prototype = control instanceof HTMLInputElement
          ? HTMLInputElement.prototype
          : control instanceof HTMLTextAreaElement
            ? HTMLTextAreaElement.prototype
            : HTMLSelectElement.prototype;
        Object.getOwnPropertyDescriptor(prototype, 'value')?.set?.call(control, saved.value);
        if (control instanceof HTMLInputElement && saved.checked !== undefined) {
          Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'checked')?.set?.call(control, saved.checked);
        }
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, 250);
  } catch {
    // Un brouillon corrompu est ignoré ; il ne contient jamais de mot de passe.
  }
}

export function PwaStatus(): JSX.Element | null {
  const native = isNativeAndroid();
  const online = useOnlineStatus();
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => native || isStandalone());
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [offlineActionBlocked, setOfflineActionBlocked] = useState(false);
  const isIos = useMemo(() => /iphone|ipad|ipod/i.test(navigator.userAgent), []);
  const updateServiceWorker = useMemo(
    () => native
      ? async (_reloadPage?: boolean) => undefined
      : registerSW({
        immediate: true,
        onNeedRefresh: () => setUpdateAvailable(true),
      }),
    [native],
  );

  useEffect(() => {
    if (native) return;
    restoreFormDraft();
    const onInstallPrompt = (event: Event) => {
      event.preventDefault();
      if (!isStandalone()) setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };
    const onBlocked = () => {
      setOfflineActionBlocked(true);
      window.setTimeout(() => setOfflineActionBlocked(false), 5_000);
    };
    window.addEventListener('beforeinstallprompt', onInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('mytag:offline-action-blocked', onBlocked);
    return () => {
      window.removeEventListener('beforeinstallprompt', onInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('mytag:offline-action-blocked', onBlocked);
    };
  }, [native]);

  async function install(): Promise<void> {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') setInstalled(true);
    setInstallPrompt(null);
  }

  if (native || (online && installed && !updateAvailable && !offlineActionBlocked)) return null;

  return (
    <div className="pwa-status fixed inset-x-3 bottom-3 z-[1000] mx-auto max-w-xl space-y-2" aria-live="polite">
      {!online ? (
        <div className="rounded-lg border border-warning/40 bg-card p-3 shadow-xl">
          <div className="flex gap-3">
            <WifiOff className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
            <div>
              <p className="text-sm font-semibold">Hors connexion</p>
              <p className="text-xs text-muted-foreground">Les positions éventuellement visibles sont anciennes. Les actions serveur et exports sont désactivés, et aucune mutation ne sera mise en attente.</p>
            </div>
          </div>
        </div>
      ) : null}

      {offlineActionBlocked ? (
        <div className="rounded-lg border border-warning/40 bg-card p-3 text-sm shadow-xl">Action bloquée : reconnectez-vous avant de contacter le serveur.</div>
      ) : null}

      {!installed && installPrompt ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3 shadow-xl">
          <div><p className="text-sm font-semibold">Installer MyTag</p><p className="text-xs text-muted-foreground">Ouvrir l’application en mode autonome.</p></div>
          <Button size="sm" icon={<Download className="h-4 w-4" />} onClick={() => void install()}>Installer</Button>
        </div>
      ) : null}

      {!installed && isIos ? (
        <div className="rounded-lg border border-border bg-card p-3 shadow-xl">
          <div className="flex items-center justify-between gap-3">
            <div><p className="text-sm font-semibold">Installer sur iPhone ou iPad</p><p className="text-xs text-muted-foreground">Safari n’affiche pas de bouton d’installation automatique.</p></div>
            <Button variant="outline" size="sm" onClick={() => setShowIosHelp((value) => !value)}>{showIosHelp ? 'Masquer' : 'Instructions'}</Button>
          </div>
          {showIosHelp ? <p className="mt-2 flex gap-2 text-xs text-muted-foreground"><Share className="h-4 w-4 shrink-0" />Dans Safari, touchez Partager, puis « Sur l’écran d’accueil » et « Ajouter ».</p> : null}
        </div>
      ) : null}

      {updateAvailable ? (
        <div className="rounded-lg border border-primary/30 bg-card p-3 shadow-xl">
          <div className="flex items-start justify-between gap-3">
            <div><p className="text-sm font-semibold">Une nouvelle version est disponible</p><p className="text-xs text-muted-foreground">Le rechargement est volontaire. Les champs non sensibles de la page courante seront restaurés.</p></div>
            <button type="button" aria-label="Plus tard" onClick={() => setUpdateAvailable(false)}><X className="h-4 w-4" /></button>
          </div>
          <Button className="mt-2" size="sm" icon={<RefreshCw className="h-4 w-4" />} onClick={() => { saveFormDraft(); void updateServiceWorker(true); }}>Recharger</Button>
        </div>
      ) : null}
    </div>
  );
}
